
import { format } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { ChevronDown, User, UserCog, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ExercisesTable } from "./ExercisesTable";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

interface SessionItemProps {
  session: any;
  isOpen: boolean;
  onToggle: () => void;
  onDelete?: (id: string) => Promise<void>;
}

export const SessionItem = ({ session, isOpen, onToggle, onDelete }: SessionItemProps) => {
  // Usando a nova coluna created_by_type para determinar quem criou a sessão
  const isStudentSession = session.created_by_type === 'student';
  const { language } = useLanguage();
  const { t } = useTranslations("student-sessions");
  
  const dateLocale = language === 'pt' ? pt : enUS;

  return (
    <Card className="border border-slate-100/50 bg-white/90 backdrop-blur-lg shadow-sm hover:shadow-md transition-all dark:bg-gray-800/90 dark:border-gray-700/50">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/20 dark:hover:bg-gray-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold leading-none tracking-tight dark:text-white">
                    {session.workout?.title || "Sem título"}
                  </h3>
                  <Badge variant="outline" className="dark:text-gray-200 dark:border-gray-600">{t("session.day_prefix")} {session.workout_day}</Badge>
                </div>
                <p className="text-sm text-muted-foreground dark:text-gray-400">
                  {format(new Date(session.created_at), "PPp", { locale: dateLocale })}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex flex-col sm:items-end gap-2">
                  <div className="flex items-center gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge 
                            variant={isStudentSession ? "warning" : "default"}
                            className="cursor-help"
                          >
                            {isStudentSession ? (
                              <User className="h-3 w-3 mr-1" />
                            ) : (
                              <UserCog className="h-3 w-3 mr-1" />
                            )}
                            {isStudentSession ? t("session.recordedByStudent") : t("session.recordedByPersonal")}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                          <p>
                            {isStudentSession
                              ? t("session.recordedByStudent") 
                              : `${t("session.recordedByPersonal")} ${session.trainer?.first_name} ${session.trainer?.last_name}`}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {isStudentSession && onDelete && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive dark:text-red-400 dark:hover:text-red-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(session.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">{t("actions.delete")}</span>
                      </Button>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="dark:text-gray-300">
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                  <span className="sr-only">{t("actions.viewDetails")}</span>
                </Button>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {session.notes && (
              <div className="mb-4">
                <h4 className="font-medium mb-2 dark:text-white">{t("form.notes")}:</h4>
                <p className="text-sm text-muted-foreground dark:text-gray-400">{session.notes}</p>
              </div>
            )}
            <ExercisesTable exercises={session.workout?.exercises || []} />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
